import {
  DashboardState,
  EScreenType,
  ESdChangeReason,
  EUserAccessPart,
  IProject,
  IScreenDesignInfo,
  LeftPanelType,
  RightPanelType,
  ViewPart,
  Visibility
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import { connect } from "react-redux";
import {
  DESIGNER_LIVE_PENDING_EVENT,
  type IDesignerLivePendingDetail
} from "../../../../services/designer-live/designer-sync-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import chatbotEvents from "../../../../shared/utility/chatbot-events";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import {
  MCP_BACKEND_NOTIFICATION_EVENT,
  type BackendNotificationPayload,
  type BackendNotificationTarget
} from "../../../../shared/utility/mcp-backend-notifications";
import { showDomainAccessRequiredModal } from "../../../../store/domain-access-modal/actions";
import { setNextCallbackFunction } from "../../../../store/unsaved-changes-modal/actions";
import "./toolbar-left.scss";
import { ToolbarMenuItem } from "./toolbar-menu-item";

export interface IToolbarLeftProps {
  setNextCallbackFunction: (cb?: () => void) => void;
  showDomainAccessRequiredModal: (actionName: string | string[]) => void;
}

export interface IToolbarLeftState {
  /** toolbar icon name → stacked notifications */
  notifications: Record<string, BackendNotificationPayload[]>;
  /** toolbar tab names with deferred designer-live reloads (badge until applied) */
  designerLivePendingTabs: string[];
}

const TOOLBAR_TARGET_MAP: Record<string, string> = {
  "toolbar-datasources": "datasources",
  "toolbar-scheduled-jobs": "scheduled-jobs",
  "toolbar-api-management": "api-management"
};

const SHIMMER_DURATION_MS = 8000;

class ToolbarLeft extends PureSdComponent<IToolbarLeftProps & IWithSDProps & IWithContextProp, IToolbarLeftState> {
  private shimmerTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();

  constructor(props: IToolbarLeftProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      notifications: {},
      designerLivePendingTabs: []
    };
  }

  componentDidMount() {
    window.addEventListener(MCP_BACKEND_NOTIFICATION_EVENT, this.handleBackendNotification as EventListener);
    window.addEventListener(DESIGNER_LIVE_PENDING_EVENT, this.handleDesignerLivePending as EventListener);
  }

  componentWillUnmount() {
    window.removeEventListener(MCP_BACKEND_NOTIFICATION_EVENT, this.handleBackendNotification as EventListener);
    window.removeEventListener(DESIGNER_LIVE_PENDING_EVENT, this.handleDesignerLivePending as EventListener);
    this.shimmerTimers.forEach((timer) => clearTimeout(timer));
    this.shimmerTimers.clear();
  }

  /** Designer-live deferred-reload badge — the event always carries the FULL pending tab set. */
  handleDesignerLivePending = (event: CustomEvent<IDesignerLivePendingDetail>) => {
    this.setState({ designerLivePendingTabs: event.detail.tabs });
  };

  handleBackendNotification = (event: CustomEvent<BackendNotificationPayload>) => {
    const payload = event.detail;
    const target = payload.target as BackendNotificationTarget;

    // Panel-datasources: open the left panel datasources tab (not the toolbar view)
    if (target === "panel-datasources" && payload.openOnly) {
      const { sd } = this.props;
      sd.setActiveLeftPanelByClick(LeftPanelType.Datasources);
      return;
    }

    // Panel notifications with items are handled by the panel component itself
    if (target.startsWith("panel-")) return;

    const toolbarName = TOOLBAR_TARGET_MAP[target];
    if (!toolbarName) return; // other panel notifications handled by their own components

    this.setState((prev) => {
      const existing = prev.notifications[toolbarName] || [];
      return {
        notifications: {
          ...prev.notifications,
          [toolbarName]: [...existing, payload]
        }
      };
    });

    // Clear existing timer and set a new one to remove shimmer after duration
    const existingTimer = this.shimmerTimers.get(toolbarName);
    if (existingTimer) clearTimeout(existingTimer);

    this.shimmerTimers.set(
      toolbarName,
      setTimeout(() => {
        this.setState((prev) => {
          const updated = { ...prev.notifications };
          delete updated[toolbarName];
          return { notifications: updated };
        });
        this.shimmerTimers.delete(toolbarName);
      }, SHIMMER_DURATION_MS)
    );
  };

  async reloadScreenList() {
    this.setState({ isScreensLoading: true } as any);
    try {
      await this.props.sd.setScreenList();
    } catch (e) {
      KMainFunctions.exceptionHandler(e);
    } finally {
      this.setState({ isScreensLoading: false } as any);
    }
  }

  handleRedirect = async (
    targetState: DashboardState,
    extraSetup: () => void = () => {},
    preCheck: boolean = false,
    loadScreen: boolean = false
  ) => {
    const { sd } = this.props;
    if (sd.dashboardState === targetState) return;

    chatbotEvents.setChatbotVisibility(false);

    if (sd.isLeftPanelCollapsed[sd.dashboardState]) {
      sd.setLeftPanelCollapsed(false);
    }

    // Decided on the click: with an editor open, `redirect` runs later, from inside the editor as it
    // closes, and by then what this reads may have moved.
    const isReturningToDesignFromReport: boolean =
      targetState === DashboardState.design &&
      (sd.dashboardState === DashboardState.reportDesigner ||
        sd.isReportDesignPageSelected() ||
        this.isCurrentScreenReportType() ||
        sd.dashboardState === DashboardState.branching);

    // Both branches below run all of this — the unsaved-changes one once the open editor has closed.
    // The tool's own preparation used to sit in the direct branch alone, so entering the Report
    // Designer with an editor open skipped its landing and left the UI screen on the report canvas,
    // and leaving it that way kept the report loaded under UI Design.
    const redirect = async (): Promise<void> => {
      if (isReturningToDesignFromReport) {
        await this.loadScreen();
      } else if (targetState === DashboardState.reportDesigner) {
        await this.prepareReportDesignerEntry();
      }
      extraSetup();
      if (targetState !== DashboardState.notificationTemplate) {
        this.props.sd.clearNotificationTemplatePanel();
      }
      if (targetState !== DashboardState.scheduledJobs) {
        this.props.sd.cancelEditingScheduledJob();
      }
      this.props.sd.cancelEditingApi();
      this.props.sd.cancelActiveMethod();
      this.props.sd.cancelCreatingOrEditingDatasource();
      this.props.sd.cancelCreatingOrEditingConfiguration();
      this.props.sd.clearSelectedWf();
      if (loadScreen) {
        await sd.saveScreen(true);
        await this.reloadScreenList();
      }
      if (targetState === DashboardState.reportDesigner) {
        this.props.sd.enterReportDesigner({ showLanding: true });
      } else {
        sd.setDashboardState(targetState);
      }
    };

    if (preCheck || this.hasUnsavedChanges()) {
      this.props.setNextCallbackFunction(redirect);
    } else {
      await redirect();
    }
  };

  hasUnsavedChanges = (): boolean => {
    const { sd } = this.props;
    return !!(
      sd?.activeConfiguration ||
      sd?.activeEditingDatasource ||
      sd?.activeCreatingDatasourceType !== undefined ||
      sd?.creatingActionsDatasource ||
      sd?.editingTable ||
      sd?.editingAction ||
      sd?.activeCreatingTableName ||
      sd?.activeCreatingActionName ||
      sd?.editingNotificationTemplate ||
      sd?.selectedSjId ||
      sd?.getSelectedApi() ||
      sd.dashboardState === DashboardState.runSettings
    );
  };

  isCurrentScreenReportType = (): boolean => {
    const { sd } = this.props;
    const currentScreen = sd.screenList?.find((x) => x.id === sd.sdId);
    return currentScreen?.publicMetaData.screenType === EScreenType.Report;
  };

  /**
   * Saves the outgoing screen and remembers it as the one UI Design returns to (`loadScreen`).
   *
   * The save is awaited — leaving Process Automation it reads the BPMN modeler before that tool
   * unmounts — and is a no-op when nothing changed. The `lastVisitedScreenId` write is not: nothing
   * on the way in reads it, and waiting for a whole-project PATCH held the landing back by more than
   * a second on every entry. It is skipped when the value is already this screen, which also spares
   * every collaborator the project reload each `project.changed` costs them.
   *
   * Known seam: `updateProject` puts its clone into state when the PATCH answers, so a project edit
   * made inside that window is dropped from local state (never from the server) until a reload.
   */
  prepareReportDesignerEntry = async (): Promise<void> => {
    await this.props.sd.saveScreen(true);

    const { sd } = this.props;
    const currentScreenId: string = sd.sdId;
    const isReturnableScreen: boolean =
      !sd.isReportDesignPageSelected() &&
      !!currentScreenId &&
      sd.screenList.some(
        (screen: IScreenDesignInfo): boolean =>
          screen.id === currentScreenId &&
          screen.publicMetaData.screenType !== EScreenType.Report &&
          screen.publicMetaData.screenType !== EScreenType.Master &&
          screen.publicMetaData.screenType !== EScreenType.Component
      );

    if (isReturnableScreen && sd.project.lastVisitedScreenId !== currentScreenId) {
      const project: IProject = _.cloneDeep(sd.project);
      project.lastVisitedScreenId = currentScreenId;
      void sd.updateProject(project, true);
    }
  };

  loadScreen = async () => {
    const { sd } = this.props;
    const isSelectedScreenIsReportDesign = sd.isReportDesignPageSelected();
    const isComingFromReportDesigner = sd.dashboardState === DashboardState.reportDesigner;
    const isCurrentScreenReport = this.isCurrentScreenReportType();

    if (isSelectedScreenIsReportDesign || isComingFromReportDesigner || isCurrentScreenReport) {
      if (isSelectedScreenIsReportDesign || sd.dashboardState === DashboardState.branching) {
        await sd.saveScreen(true);
      }
      await this.reloadScreenList();

      const { screenList, project } = sd;
      const { lastVisitedScreenId } = project;

      let screenIdToLoad = lastVisitedScreenId;

      if (lastVisitedScreenId) {
        const lastVisitedScreen = screenList.find((x) => x.id === lastVisitedScreenId);
        const isLastVisitedScreenReport = lastVisitedScreen?.publicMetaData.screenType === EScreenType.Report;

        if (isLastVisitedScreenReport) {
          screenIdToLoad = screenList.find(
            (x) =>
              x.publicMetaData.screenType !== EScreenType.Report &&
              x.publicMetaData.screenType !== EScreenType.Master &&
              x.publicMetaData.screenType !== EScreenType.Component
          )?.id;
        }
      } else {
        screenIdToLoad = screenList.find(
          (x) =>
            x.publicMetaData.screenType !== EScreenType.Report &&
            x.publicMetaData.screenType !== EScreenType.Master &&
            x.publicMetaData.screenType !== EScreenType.Component
        )?.id;
      }

      if (screenIdToLoad) {
        sd.loadScreen(screenIdToLoad, true);
      }
    }
  };

  setCanvasHeight = () => {
    const kuika_iframeWrapper: HTMLIFrameElement = document.getElementsByClassName(
      "kuika_iframeWrapper"
    )[0] as HTMLIFrameElement;
    const activeCanvasIFrame: HTMLIFrameElement = document.getElementById(
      "kuika_iframeAgent_canvas"
    ) as HTMLIFrameElement;
    activeCanvasIFrame.style.height = `calc(100vh - 136px)`;
    kuika_iframeWrapper.style.height = `calc(100vh - 136px)`;
  };

  resetCanvasHeight = () => {
    const kuika_iframeWrapper: HTMLIFrameElement = document.getElementsByClassName(
      "kuika_iframeWrapper"
    )[0] as HTMLIFrameElement;
    const activeCanvasIFrame: HTMLIFrameElement = document.getElementById(
      "kuika_iframeAgent_canvas"
    ) as HTMLIFrameElement;

    if (kuika_iframeWrapper && activeCanvasIFrame) {
      activeCanvasIFrame.style.height = "";
      kuika_iframeWrapper.style.height = "";

      kuika_iframeWrapper.style.maxWidth = "";
      kuika_iframeWrapper.style.minWidth = "";
      activeCanvasIFrame.style.maxWidth = "";
      activeCanvasIFrame.style.minWidth = "";
    }
  };

  /** The full tab list (all dashboards, each with its own `condition` gate). */
  buildMenuDescriptors = () => {
    const { sd } = this.props;

    const menuItems = [
      {
        name: "ui-design",
        label: "UI Design",
        onboardingId: "header_toolbar_ui-design",
        condition: sd.getVisibility(ViewPart.DesignerButton) === Visibility.Visible,
        active: sd.dashboardState === DashboardState.design,
        onClick: () =>
          this.handleRedirect(DashboardState.design, () => {
            if (sd.dashboardState === DashboardState.reportDesigner) {
              this.resetCanvasHeight();
            }
            sd.setActiveRightPanelByClick(RightPanelType.Properties);
          })
      },
      {
        name: "datasources",
        label: "Datasources",
        onboardingId: "header_toolbar_datasources",
        condition: KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
          EUserAccessPart.ManageDatasource,
          sd.project?.projectAccess
        ),
        active: sd.dashboardState === DashboardState.dataDiagramScheme,
        onClick: () => this.handleRedirect(DashboardState.dataDiagramScheme, () => {})
      },
      {
        name: "ai-hub",
        label: "AI Functions",
        onboardingId: "header_toolbar_aihub",
        condition: KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
          EUserAccessPart.ManageDatasource,
          sd.project?.projectAccess
        ),

        active: sd.dashboardState === DashboardState.aiHub,
        onClick: () =>
          // AI Functions has no left panel — the centre table lists everything —
          // so only the data load matters here. (setActiveLeftPanelByClick had no
          // aiHub branch and could never have taken effect anyway.)
          this.handleRedirect(DashboardState.aiHub, async () => {
            await sd.loadActionDefinitions(true);
          })
      },
      {
        name: "process-automation",
        label: "Process Automation",
        onboardingId: "header_toolbar_process-automation",
        condition:
          KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
            EUserAccessPart.ManageWorkflow,
            sd.project?.projectAccess
          ) && sd.getVisibility(ViewPart.WorkFlowButton) === Visibility.Visible,
        active: sd.dashboardState === DashboardState.workFlow,
        onClick: () =>
          this.handleRedirect(DashboardState.workFlow, () => {
            sd.setActiveWorkFlowLeftPanel(LeftPanelType.WorkFlowList);
            sd.setActiveRightPanelByClick(RightPanelType.Properties);
          })
      },
      {
        name: "email-builder",
        label: "Email Builder",
        onboardingId: "header_toolbar_email-builder",
        condition:
          KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
            EUserAccessPart.ManageEmailDesigns,
            sd.project?.projectAccess
          ) && sd.getVisibility(ViewPart.NotificationTemplateButton) === Visibility.Visible,
        active: sd.dashboardState === DashboardState.notificationTemplate,
        onClick: () =>
          this.handleRedirect(DashboardState.notificationTemplate, () => {
            sd.setCreateNotificationTemplateActive();
          })
      },
      {
        name: "report-designer",
        label: "Report Designer",
        onboardingId: "header_toolbar_report-designer",
        condition:
          KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
            EUserAccessPart.ManageReportDesigns,
            sd.project?.projectAccess
          ) && sd.getVisibility(ViewPart.ReportDesignerButton) === Visibility.Visible,
        active: sd.dashboardState === DashboardState.reportDesigner,
        onClick: () =>
          this.handleRedirect(DashboardState.reportDesigner, () => {
            this.setCanvasHeight();
          })
      },
      {
        name: "scheduled-jobs",
        label: "Scheduled Jobs",
        onboardingId: "header_toolbar_scheduled-jobs",
        condition:
          KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
            EUserAccessPart.ManageScheduledJobsActions,
            sd.project?.projectAccess
          ) && sd.getVisibility(ViewPart.ScheduledJobsButton) === Visibility.Visible,
        active: sd.dashboardState === DashboardState.scheduledJobs,
        onClick: () =>
          this.handleRedirect(DashboardState.scheduledJobs, async () => {
            sd.setCreateSJobActive(true);
            await sd.loadActionDefinitions(true);
          })
      },
      {
        name: "api-management",
        label: "API Builder",
        onboardingId: "header_toolbar_api-management",
        condition:
          KMainFunctions.hasUserAccessInWorkspaceByProjectRole(EUserAccessPart.ManageAPI, sd.project?.projectAccess) &&
          sd.getVisibility(ViewPart.ApiManagementButton) === Visibility.Visible,
        active: sd.dashboardState === DashboardState.apiManagement,
        onClick: () =>
          this.handleRedirect(DashboardState.apiManagement, () => {
            sd.setActiveApiManagementPanel(LeftPanelType.ApiManagement);
          })
      },
      {
        name: "config-manager",
        label: "Config Manager",
        onboardingId: "header_toolbar_config-manager",
        condition:
          KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
            EUserAccessPart.ManageConfigurations,
            sd.project?.projectAccess
          ) && sd.getVisibility(ViewPart.ConfigManagerButton) === Visibility.Visible,
        active: sd.dashboardState === DashboardState.configManager,
        onClick: () =>
          this.handleRedirect(DashboardState.configManager, () => {
            sd.cancelCreatingOrEditingConfiguration();
          })
      }
    ];
    // No Branching tab (header redesign): the version-control page is reached through the
    // left-region branch pill (Activate / branch popover), not the dashboard strip.

    return menuItems;
  };

  /** Render one tab, applying its live notification/reload badges from state. */
  renderMenuItem = (descriptor: ReturnType<ToolbarLeft["buildMenuDescriptors"]>[number]) => {
    const { notifications, designerLivePendingTabs } = this.state;
    const { name, label, condition, active, onClick, onboardingId } = descriptor;
    const itemNotifications = notifications[name] || [];
    const hasDeleteNotif = itemNotifications.some((n) => n.isDelete);
    const hasAnyNotif = itemNotifications.length > 0;
    const hasPendingReload = designerLivePendingTabs.includes(name);

    return (
      <ToolbarMenuItem
        key={name}
        name={name}
        label={label}
        condition={condition}
        active={active}
        onClick={onClick}
        onboardingId={onboardingId}
        data-testid={`header${name}tab`}
        shimmer={hasAnyNotif || hasPendingReload}
        shimmerDelete={hasDeleteNotif}
        notifications={itemNotifications}
      />
    );
  };

  // Plain flat row of 48x48 icon tabs, with ONE exception: the active tab grows into a labeled
  // pill (icon + dashboard name on a `--blue-1` fill — see `--menu-item--active` in
  // toolbar-left.scss). No hover-preview and no sliding underline: the fill IS the marker.
  // Centring is NOT this component's job: the toolbar's three-column grid owns it
  // (`kuika-toolbar__region--center`), so the row stays centred as tabs appear and disappear
  // with the user's access — and re-centres itself when the pill moves to another tab.
  render() {
    return <div className="kuika-toolbar__menu-items">{this.buildMenuDescriptors().map(this.renderMenuItem)}</div>;
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const mapDispatchToProps = (dispatch) => ({
  showDomainAccessRequiredModal: (accessName) => dispatch(showDomainAccessRequiredModal(accessName)),
  setNextCallbackFunction: (cb?: () => void) => dispatch(setNextCallbackFunction(cb))
});

const toolbarLeft = connect(
  null,
  mapDispatchToProps
)(withContext<IToolbarLeftProps>(withSD<IToolbarLeftProps>(ToolbarLeft)));
export { toolbarLeft as ToolbarLeft };
